import { Dispatch, SetStateAction, useCallback, useMemo, useContext } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { useStyles } from "./useCorporationsColumns.styles";
import useCorporationsLicenses, { ModuleLicenseStatusEnum } from "../useCorporationsLicenses";
import { IMainTableColumn } from "components/MainTable/MainTableInterfaces";
import { ICorporation } from "../../CorporationsAPIInterfaces";
import { TableMenu } from "components";
import { IOption } from "components/TableMenu/TableMenuInterfaces";
import { useNavigate } from "react-router-dom";
import { CORPORATE_BACKOFFICE_PATHS } from "constants/constants";
import { superAdminCorporateLogin } from "api/queries/loginAPI";
import { UserContext } from "contexts/UserContext/UserContext";
import { setToken } from "utilities/storageUtilities";
import { ILoginResponse } from "components/Login/LoginInterfaces";
import { CorporateContext } from "contexts/CorporateContext/CorporateContext";
import { MillOnboardingContext } from "contexts/MillOnboardingContext/MillOnboardingContext";

interface ICorporationsColumns extends Omit<IMainTableColumn, "render"> {
    render: (row: ICorporation) => string | JSX.Element | number;
}

const useCorporationsColumns = (
    setCorporationEditData: Dispatch<SetStateAction<ICorporation | undefined>>,
    onBirchToggleClick: (row: ICorporation, enable: boolean) => void,
    onModuleLicenseToggleClick: (row: ICorporation, enable: boolean) => void,
): ICorporationsColumns[] => {
    const classes = useStyles();
    const { t } = useTranslation();
    const { handleTokenAvailable } = useContext(UserContext);
    const { isOnboardingFetch } = useContext(MillOnboardingContext);
    const { getModuleLicenseStatus } = useCorporationsLicenses();
    const { refreshCorporationsAndDivisions } = useContext(CorporateContext);

    const renderBirchOptions = useCallback(
        (row: ICorporation) => {
            const isRcaEnabled = row.rca_enabled ?? false;
            return {
                name: isRcaEnabled ? t("setup_corporation.deactivate_birch") : t("setup_corporation.activate_birch"),
                callback: () => onBirchToggleClick(row, !isRcaEnabled),
                customStyles: { fontWeight: 600 },
            };
        },
        [onBirchToggleClick, t],
    );

    const renderLicenseOptions = useCallback(
        (row: ICorporation) => {
            const licenseStatus = getModuleLicenseStatus(row);

            if (licenseStatus === ModuleLicenseStatusEnum.Active) {
                return {
                    name: t("setup_corporation.deactivate"),
                    callback: () => onModuleLicenseToggleClick(row, false),
                    customStyles: { fontWeight: 600 },
                };
            }
            return {
                name:
                    licenseStatus === ModuleLicenseStatusEnum.No_Activated
                        ? t("setup_corporation.activate")
                        : t("setup_corporation.reactivate"),
                callback: () => onModuleLicenseToggleClick(row, true),
                customStyles: { fontWeight: 600 },
            };
        },
        [getModuleLicenseStatus, onModuleLicenseToggleClick, t],
    );

    const navigate = useNavigate();
    const columns = useMemo(
        (): ICorporationsColumns[] => [
            {
                id: "name",
                label: t("setup_corporation.corporation_table_headers.name"),
                hideSortIcon: false,
                render: (row) => row.name,
            },
            {
                id: "logo",
                label: t("setup_corporation.corporation_table_headers.logo"),
                hideSortIcon: true,
                render: (row) => (
                    <div className={classes.userImageWrapper}>
                        {row.media ? (
                            <img src={row.media?.media_url} alt="user" className={classes.userImage} />
                        ) : (
                            <span>OP</span>
                        )}
                    </div>
                ),
            },
            {
                id: "admin",
                label: t("setup_corporation.corporation_table_headers.admin"),
                hideSortIcon: true,
                render: (row) => {
                    return row.users.map((user, index) => (
                        <div key={index}>
                            <span>
                                {user.first_name} {user.last_name}
                            </span>
                        </div>
                    ));
                },
            },
            {
                id: "created_at",
                label: t("setup_corporation.corporation_table_headers.created_at"),
                hideSortIcon: true,
                render: (row) => moment(row.created_at).format("YYYY/MM/DD"),
            },
            {
                id: "account_status",
                label: t("setup_corporation.corporation_table_headers.account_status"),
                hideSortIcon: true,
                render: (row): any => (
                    <span className={classes.statusButton}>
                        {getModuleLicenseStatus(row) === ModuleLicenseStatusEnum.Active
                            ? t("setup_corporation.active")
                            : t("setup_corporation.inactive")}
                    </span>
                ),
            },
            {
                id: "birch",
                label: t("setup_corporation.corporation_table_headers.birch"),
                hideSortIcon: true,
                render: (row): any => (
                    <span className={classes.statusButton}>
                        {row.rca_enabled ? t("setup_corporation.active") : t("setup_corporation.inactive")}
                    </span>
                ),
            },
            {
                id: "options",
                label: "",
                hideSortIcon: true,
                render: (row) => {
                    const options: IOption[] = [
                        { ...renderLicenseOptions(row) },
                        { ...renderBirchOptions(row) },
                        {
                            name: t("setup_corporation.edit"),
                            callback: () => setCorporationEditData(row),
                        },
                        {
                            name: t("setup_corporation.login"),
                            callback: async () => {
                                let loginResponse = await superAdminCorporateLogin({
                                    corporate_id: row.corporate_id,
                                });

                                const { data } = loginResponse as ILoginResponse;

                                if (data.token) {
                                    setToken(data.token);
                                    handleTokenAvailable(true);

                                    await isOnboardingFetch();
                                    await refreshCorporationsAndDivisions();
                                    if (row.mills?.length === 1) {
                                        navigate(
                                            `/${CORPORATE_BACKOFFICE_PATHS.ROOT_PATH}/${CORPORATE_BACKOFFICE_PATHS.LOCATIONS.ROOT}/${row.mills[0].mill_id}`,
                                        );
                                    } else {
                                        navigate(
                                            `/${CORPORATE_BACKOFFICE_PATHS.ROOT_PATH}/${CORPORATE_BACKOFFICE_PATHS.CORPORATE_DASHBOARD}`,
                                        );
                                    }
                                }
                            },
                        },
                    ];
                    return <TableMenu options={options} />;
                },
            },
        ],
        [
            t,
            classes.userImageWrapper,
            classes.userImage,
            classes.statusButton,
            handleTokenAvailable,
            getModuleLicenseStatus,
            isOnboardingFetch,
            renderLicenseOptions,
            renderBirchOptions,
            navigate,
            setCorporationEditData,
            refreshCorporationsAndDivisions,
        ],
    );

    return columns;
};

export default useCorporationsColumns;
